import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Calendar } from '@vendasta/meetings';
import { Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pluck,
  switchMap,
} from 'rxjs/operators';
import { BookMeNowStoreService, Errors } from '../book-me-now-store.service';
import { routeTransitions } from './../route-transitions';
import { stripBookingFlowParams } from '../util';

@Component({
  selector: 'app-bookme',
  templateUrl: './bookme.component.html',
  styleUrls: ['./bookme.component.scss'],
  animations: [routeTransitions],
  standalone: false,
})
export class BookmeComponent implements OnInit, OnDestroy {
  calendarId: string;
  groupId: string;
  serviceId: string;
  sessionId: string;
  sessionInviteeId: string;
  calendar$: Observable<Calendar>;
  calendar: Calendar;
  defaultLogoUrl = '/assets/images/meetingscheduler-logo.png';

  shouldDisplayError$: Observable<boolean>;
  errorDisplay$: Observable<Errors>;
  hideHeader$: Observable<boolean>;
  initializing = true;

  groupOrServiceLogoUrl: string;
  private logoUrlSubscription: Subscription;
  private subscriptions: Subscription[] = [];

  // Session error message properties
  registrationCutoffDateTime: string;
  primaryHostEmail: string;

  isMeetingInfoRoute: boolean;
  isRescheduleConfirmationRoute: boolean;

  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly route: ActivatedRoute,
    private readonly meta: Meta,
    private readonly router: Router
  ) {
    this.errorDisplay$ = this.bookmenowService.errorDisplay$;

    this.calendarId = this.route.snapshot.params.calendarId;
    this.groupId = this.route.snapshot.params.groupId;
    this.serviceId = this.route.snapshot.params.serviceId;
    this.sessionId = this.route.snapshot.params.sessionId;
    this.sessionInviteeId = this.route.snapshot.params.sessionInviteeId;

    // for session routes host is not required
    const isSessionRoute =
      this.route.snapshot.url.length > 0 &&
      this.route.snapshot.url[0].path === 'session';

    // for meeting-info routes host is not required
    this.isMeetingInfoRoute =
      this.route.snapshot.url.length > 0 &&
      this.route.snapshot.url[0].path === 'meeting-info';

    // Check for new reschedule confirmation routes (confirm-changes)
    this.isRescheduleConfirmationRoute = false;
    this.subscriptions.push(
      this.router.events.subscribe(() => {
        const url = this.router.url;
        this.isRescheduleConfirmationRoute = url.includes('/confirm-changes');
      })
    );

    // for cancel-info routes host is not required
    const isCancelInfoRoute =
      this.route.snapshot.url.length > 0 &&
      this.route.snapshot.url[0].path === 'cancel-info';

    // for reschedule-info routes host is not required
    const isRescheduleInfoRoute =
      this.route.snapshot.url.length > 0 &&
      this.route.snapshot.url[0].path === 'reschedule-info';

    // for shared meeting routes (video or meeting prefix) host is not required
    const isSharedVideoRoute =
      this.route.snapshot.url.length > 0 &&
      (this.route.snapshot.url[0].path === 'video' ||
        this.route.snapshot.url[0].path === 'meeting');

    if (
      !isSessionRoute &&
      !this.isMeetingInfoRoute &&
      !isCancelInfoRoute &&
      !isRescheduleInfoRoute &&
      !isSharedVideoRoute &&
      !this.calendarId &&
      !this.groupId &&
      !this.serviceId
    ) {
      this.initializing = false;
      this.bookmenowService.setErrorDisplay('no-host');
      return;
    }

    // Routes that don't need calendar loading
    if (
      isSessionRoute ||
      this.isMeetingInfoRoute ||
      isCancelInfoRoute ||
      isRescheduleInfoRoute ||
      isSharedVideoRoute ||
      this.groupId ||
      this.serviceId
    ) {
      this.initializing = false;
    }

    this.calendar$ = this.route.params.pipe(
      pluck('calendarId'),
      filter<string>(Boolean),
      distinctUntilChanged(),
      switchMap((calendarId) => this.bookmenowService.loadCalendar(calendarId))
    );

    this.calendar$.subscribe(
      (calendar) => {
        this.calendar = calendar;
        this.initializing = false;
      },
      (err) => {
        console.error(err);
        this.initializing = false;
        this.bookmenowService.setErrorDisplay('error-host');
      }
    );
    this.route.params
      .pipe(
        pluck('meetingTypeId'),
        distinctUntilChanged(),
        filter<string>(Boolean)
      )
      .subscribe(
        (meetingTypeId) => {
          this.bookmenowService.loadMeetingType(meetingTypeId);
        },
        (err) => {
          this.bookmenowService.handleMeetingTypeError(err);
        }
      );
    // Hide header for cancel-info/reschedule-info/shared-video routes or when hide_header query param is set
    this.hideHeader$ = this.route.queryParams.pipe(
      map(
        (params) =>
          params?.hide_header === 'true' ||
          isCancelInfoRoute ||
          isRescheduleInfoRoute ||
          isSharedVideoRoute
      )
    );
  }

  ngOnInit(): void {
    this.logoUrlSubscription =
      this.bookmenowService.businessDisplayLogoUrl$.subscribe((url) => {
        this.groupOrServiceLogoUrl = url;
      });
    this.shouldDisplayError$ = this.errorDisplay$.pipe(
      map((errorDisplay) => {
        return Boolean(errorDisplay);
      })
    );
    if (
      typeof location !== 'undefined' &&
      location.href.includes('meow=true')
    ) {
      this.defaultLogoUrl = '/assets/images/bookmeow.png';
    }
  }
  ngOnDestroy(): void {
    if (this.logoUrlSubscription) {
      this.logoUrlSubscription.unsubscribe();
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  gotoCalendar(): void {
    // Navigate to the base route (calendar child) by stripping /contact-form or /confirmation
    const baseUrl = this.router.url
      .replace(/\/contact-form.*/, '')
      .replace(/\/confirmation.*/, '')
      .split('?')[0];
    const currentQueryString = stripBookingFlowParams(
      typeof location !== 'undefined' ? location.search : ''
    );
    this.router.navigateByUrl(`${baseUrl}${currentQueryString}`).then(() => {
      this.bookmenowService.setErrorDisplay(undefined);
    });
  }

  prepareRoute(outlet: RouterOutlet): string {
    // Check if outlet is activated before accessing its properties
    if (!outlet || !outlet.isActivated) {
      return 'calendar';
    }

    // Avoid ExpressionChangedAfterChecked. If we have a startTime, likely we will redirect to the contact form first
    const defaultAnimation =
      this.route.snapshot.queryParams.startTime ||
      (typeof location !== 'undefined' &&
        location.pathname.includes('contact-form'))
        ? 'form'
        : 'calendar';
    return (
      (outlet?.activatedRouteData &&
        // Default to calendar to avoid expression changed after check error
        outlet.activatedRouteData.animationState) ||
      defaultAnimation
    );
  }

  onImageLoaded(e: Event): void {
    if (
      !e.currentTarget ||
      !this.meta?.updateTag ||
      !('height' in e.currentTarget && 'width' in e.currentTarget)
    ) {
      return;
    }
    const { width, height } = e.currentTarget as HTMLImageElement;
    if (width && height) {
      this.meta.updateTag({
        property: 'og:image:width',
        content: width.toString(),
      });
      this.meta.updateTag({
        property: 'og:image:height',
        content: height.toString(),
      });
    }
  }
}
